import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/shared/PageHeader";
import { RefreshingIndicator } from "@/components/shared/RefreshingIndicator";
import { StateCard } from "@/components/shared/StateCard";
import { TableSkeleton } from "@/components/shared/TableSkeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  createProjectPayment,
  createClient,
  createProject,
  deleteClient,
  fetchClientById,
  type ProjectPaymentMethod,
  type ClientItem,
  type ProjectLabel,
  updateClient,
} from "@/lib/projects-clients";
import { formatUsPhoneDisplay, normalizeUsPhoneDigits } from "@/lib/us-phone";
import { useClients, useProjectLabels } from "@/hooks/use-app-queries";
import { invalidateClientData, invalidateProjectData } from "@/lib/query-invalidation";
import { useToast } from "@/hooks/use-toast";
import {
  AlertTriangle,
  FileX,
  Info,
  Mail,
  MoreHorizontal,
  Phone,
  Plus,
  Search,
  Trash2,
  User,
  Users,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

type ClientForm = {
  name: string;
  companyName: string;
  type: string;
  email: string;
  phone: string;
  address: string;
};

type ProjectFlowForm = {
  name: string;
  code: string;
  clientId: string;
  jobSiteAddress: string;
  labelIds: string[];
};

type ClientProjectsResponse = {
  projects?: Array<{
    id: string;
    name: string;
    isActive?: boolean;
  }>;
};

const emptyForm: ClientForm = {
  name: "",
  companyName: "",
  type: "General Contractor",
  email: "",
  phone: "",
  address: "",
};

const emptyProjectForm: ProjectFlowForm = {
  name: "",
  code: "",
  clientId: "",
  jobSiteAddress: "",
  labelIds: [],
};

const PAYMENT_METHOD_OPTIONS: Array<{ value: ProjectPaymentMethod; label: string }> = [
  { value: "CASH", label: "paymentMethods.cash" },
  { value: "CHECK", label: "paymentMethods.check" },
  { value: "CARD", label: "paymentMethods.card" },
  { value: "ZELLE", label: "paymentMethods.zelle" },
  { value: "ACH", label: "paymentMethods.ach" },
  { value: "OTHER", label: "paymentMethods.other" },
];

function CompactClientCard({
  client,
  actions,
  onOpen,
}: {
  client: ClientItem;
  actions: ReactNode;
  onOpen: () => void;
}) {
  return (
    <div
      className="rounded-lg border bg-card p-4 cursor-pointer"
      onClick={onOpen}
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <User className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate text-sm font-semibold text-accent" title={client.name}>
              {client.name}
            </span>
          </div>
          {client.companyName ? (
            <p className="mt-1 truncate text-xs text-muted-foreground" title={client.companyName}>
              {client.companyName}
            </p>
          ) : null}
        </div>
        <div className="shrink-0 self-stretch" onClick={(event) => event.stopPropagation()}>
          {actions}
        </div>
      </div>
    </div>
  );
}

export default function Clients() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();

  const [search, setSearch] = useState("");
  const [clientHelpOpen, setClientHelpOpen] = useState(false);

  const [openModal, setOpenModal] = useState(false);
  const [editingClientId, setEditingClientId] = useState<string | null>(null);
  const [form, setForm] = useState<ClientForm>(emptyForm);
  const [projectForm, setProjectForm] = useState<ProjectFlowForm>(emptyProjectForm);
  const [projectLabelSearch, setProjectLabelSearch] = useState("");
  const [clientModalStep, setClientModalStep] = useState<"client" | "project">("client");
  const [flowClient, setFlowClient] = useState<ClientItem | null>(null);
  const [saving, setSaving] = useState(false);
  const [savingAndContinuing, setSavingAndContinuing] = useState(false);
  const [savingProject, setSavingProject] = useState(false);
  const [savingProjectAndContinuing, setSavingProjectAndContinuing] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<ClientItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [paymentClient, setPaymentClient] = useState<ClientItem | null>(null);
  const [paymentProjects, setPaymentProjects] = useState<Array<{ id: string; name: string }>>([]);
  const [paymentProjectId, setPaymentProjectId] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [paymentMethod, setPaymentMethod] = useState<ProjectPaymentMethod>("CASH");
  const [paymentNote, setPaymentNote] = useState("");
  const [loadingPaymentProjects, setLoadingPaymentProjects] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);

  const clientsQuery = useClients(search);
  const projectLabelsQuery = useProjectLabels();
  const clients = clientsQuery.data ?? [];
  const projectLabels = projectLabelsQuery.data ?? [];
  const loading = clientsQuery.isLoading && !clientsQuery.data;
  const error = clientsQuery.error instanceof Error ? clientsQuery.error.message : null;
  const isRefreshing = clientsQuery.isFetching && Boolean(clientsQuery.data);
  const filteredProjectLabels = projectLabels.filter((label) =>
    label.name.toLowerCase().includes(projectLabelSearch.trim().toLowerCase()),
  );

  const editingClient = useMemo(
    () => clients.find((c) => c.id === editingClientId) ?? null,
    [clients, editingClientId],
  );
  const duplicateNameMatches = useMemo(() => {
    const normalized = form.name.trim().toLowerCase();
    if (normalized.length < 3) return [];
    return clients.filter((client) => {
      if (editingClientId && client.id === editingClientId) return false;
      const candidate = client.name.trim().toLowerCase();
      return candidate.includes(normalized) || normalized.includes(candidate);
    });
  }, [clients, form.name, editingClientId]);
  const hasClientDependencies = (client: ClientItem) =>
    Number(client._count?.projects ?? 0) > 0 || Number(client._count?.tickets ?? 0) > 0;
  const getClientTypeLabel = (value?: string | null) => {
    switch (value) {
      case "General Contractor":
        return t("clients.types.generalContractor");
      case "Owner":
        return t("clients.types.owner");
      case "Property Manager":
        return t("clients.types.propertyManager");
      case "Developer":
        return t("clients.types.developer");
      case "Other":
        return t("clients.types.other");
      default:
        return value ?? "-";
    }
  };
  const renderContactCell = (client: ClientItem, mutedBackground = false) => (
    <td className={`border-l border-border/50 px-4 py-3 ${mutedBackground ? "bg-muted/35" : ""}`}>
      <div className="space-y-1.5 text-muted-foreground">
        <div className="flex items-center gap-2 min-w-0">
          <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />
          <span className="truncate" title={client.email ?? "-"}>
            {client.email ?? "-"}
          </span>
        </div>
        <div className="flex items-center gap-2 min-w-0">
          <Phone className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />
          <span className="truncate text-muted-foreground/85" title={client.phone ? formatUsPhoneDisplay(client.phone) : "-"}>
            {client.phone ? formatUsPhoneDisplay(client.phone) : "-"}
          </span>
        </div>
      </div>
    </td>
  );

  useEffect(() => {
    if (clientsQuery.isLoading || clients.length === 0) return;
    const editId = searchParams.get("edit");
    if (!editId) return;
    const client = clients.find((c) => c.id === editId);
    if (!client) return;
    openEditModal(client);
    const next = new URLSearchParams(searchParams);
    next.delete("edit");
    setSearchParams(next, { replace: true });
  }, [clientsQuery.isLoading, clients, searchParams, setSearchParams]);

  useEffect(() => {
    const shouldOpenCreate = searchParams.get("new") === "1";
    if (!shouldOpenCreate) return;
    openCreateModal();
    const next = new URLSearchParams(searchParams);
    next.delete("new");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const openCreateModal = () => {
    setEditingClientId(null);
    setForm(emptyForm);
    setProjectForm(emptyProjectForm);
    setProjectLabelSearch("");
    setFlowClient(null);
    setClientModalStep("client");
    setOpenModal(true);
  };

  const openEditModal = (client: ClientItem) => {
    setEditingClientId(client.id);
    setForm({
      name: client.name ?? "",
      companyName: client.companyName ?? "",
      type: client.type ?? "General Contractor",
      email: client.email ?? "",
      phone: formatUsPhoneDisplay(client.phone),
      address: client.address ?? "",
    });
    setProjectForm(emptyProjectForm);
    setProjectLabelSearch("");
    setFlowClient(null);
    setClientModalStep("client");
    setOpenModal(true);
  };

  const resetClientModalState = () => {
    setOpenModal(false);
    setEditingClientId(null);
    setForm(emptyForm);
    setProjectForm(emptyProjectForm);
    setProjectLabelSearch("");
    setFlowClient(null);
    setClientModalStep("client");
    setSaving(false);
    setSavingAndContinuing(false);
    setSavingProject(false);
    setSavingProjectAndContinuing(false);
  };

  const submitForm = async (mode: "save" | "continue_to_project" = "save") => {
    if (!form.name.trim()) {
      toast({
        title: t("clients.nameRequired"),
        description: t("clients.enterClientName"),
        variant: "destructive",
      });
      return;
    }

    if (mode === "continue_to_project") {
      setSavingAndContinuing(true);
    } else {
      setSaving(true);
    }
    try {
      if (editingClientId) {
        await updateClient(editingClientId, {
          name: form.name.trim(),
          companyName: form.companyName.trim() || undefined,
          type: form.type || undefined,
          email: form.email || undefined,
          phone: normalizeUsPhoneDigits(form.phone) || undefined,
          address: form.address || undefined,
        });
        toast({ title: t("clients.clientUpdated") });
      } else {
        const created = await createClient({
          name: form.name.trim(),
          companyName: form.companyName.trim() || undefined,
          type: form.type || undefined,
          email: form.email || undefined,
          phone: normalizeUsPhoneDigits(form.phone) || undefined,
          address: form.address || undefined,
        });
        toast({
          title:
            mode === "continue_to_project"
              ? t("clients.clientCreatedContinueProject")
              : t("clients.clientCreated"),
        });

        if (mode === "continue_to_project") {
          await invalidateClientData(queryClient);
          setFlowClient(created);
          setProjectForm({
            ...emptyProjectForm,
            clientId: created.id,
            jobSiteAddress: created.address?.trim() || "",
          });
          setProjectLabelSearch("");
          setClientModalStep("project");
          return;
        }
      }

      resetClientModalState();
      await invalidateClientData(queryClient);
    } catch (e) {
      toast({
        title: editingClientId ? t("clients.updateFailed") : t("clients.createFailed"),
        description: e instanceof Error ? e.message : t("common.requestFailed"),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
      setSavingAndContinuing(false);
    }
  };

  const submitProjectStep = async (mode: "save" | "continue_to_ticket" = "save") => {
    if (!flowClient?.id) return;
    if (!projectForm.name.trim()) {
      toast({
        title: t("projects.projectNameRequired"),
        description: t("projects.enterProjectName"),
        variant: "destructive",
      });
      return;
    }

    if (mode === "continue_to_ticket") {
      setSavingProjectAndContinuing(true);
    } else {
      setSavingProject(true);
    }

    try {
      const created = await createProject({
        name: projectForm.name.trim(),
        code: projectForm.code.trim() || undefined,
        clientId: flowClient.id,
        jobSiteAddress: projectForm.jobSiteAddress.trim() || undefined,
        status: "ACTIVE",
        labelIds: projectForm.labelIds,
      });

      toast({
        title:
          mode === "continue_to_ticket"
            ? t("projects.projectCreatedContinueTicket")
            : t("projects.projectCreated"),
      });

      await invalidateProjectData(queryClient);
      resetClientModalState();

      if (mode === "continue_to_ticket") {
        navigate(`/tickets/new?projectId=${encodeURIComponent(created.id)}&type=estimate`);
      }
    } catch (e) {
      toast({
        title: t("clients.createFailed"),
        description: e instanceof Error ? e.message : t("projects.unableToSaveProject"),
        variant: "destructive",
      });
    } finally {
      setSavingProject(false);
      setSavingProjectAndContinuing(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if (hasClientDependencies(deleteTarget)) {
      toast({
        title: t("clients.deleteBlocked"),
        description: t("clients.cannotDeleteLinked"),
        variant: "destructive",
      });
      setDeleteTarget(null);
      return;
    }
    setDeleting(true);
    try {
      await deleteClient(deleteTarget.id);
      toast({ title: t("clients.clientRemoved") });
      setDeleteTarget(null);
      await invalidateClientData(queryClient);
    } catch (e) {
      toast({
        title: t("clients.deleteFailed"),
        description: e instanceof Error ? e.message : t("common.requestFailed"),
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const copyApproverEmail = async (email?: string | null) => {
    if (!email) return;
    try {
      await navigator.clipboard.writeText(email);
      toast({ title: t("clients.approverEmailCopied") });
    } catch {
      toast({
        title: t("clients.copyFailed"),
        description: t("clients.copyApproverEmailFailed"),
        variant: "destructive",
      });
    }
  };


  const renderClientActions = (client: ClientItem, triggerClassName: string) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={triggerClassName}
          onClick={(event) => event.stopPropagation()}
          aria-label={t("common.actions")}
        >
          <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
        <DropdownMenuItem onClick={(event) => { event.stopPropagation(); void openRecordPayment(client); }}>
          {t("clients.recordPayment")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={(event) => { event.stopPropagation(); navigate(`/projects?new=1&clientId=${client.id}`); }}>
          {t("clients.newProject")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={(event) => { event.stopPropagation(); navigate(`/tickets/new?clientId=${client.id}`); }}>
          {t("clients.newTicket")}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={!client.email}
          onClick={(event) => { event.stopPropagation(); void copyApproverEmail(client.email); }}
        >
          {t("clients.copyApproverEmail")}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={(event) => { event.stopPropagation(); openEditModal(client); }}>
          {t("clients.editClient")}
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={hasClientDependencies(client)}
          className="text-destructive focus:text-destructive"
          onClick={(event) => { event.stopPropagation(); setDeleteTarget(client); }}
        >
          {t("clients.deleteClient")}
        </DropdownMenuItem>
        {hasClientDependencies(client) ? (
          <DropdownMenuItem disabled className="text-xs text-muted-foreground">
            {t("clients.cannotDeleteLinked")}
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const closePaymentDialog = () => {
    setPaymentClient(null);
    setPaymentProjects([]);
    setPaymentProjectId("");
    setPaymentAmount("");
    setPaymentDate(new Date().toISOString().slice(0, 10));
    setPaymentMethod("CASH");
    setPaymentNote("");
    setLoadingPaymentProjects(false);
    setSavingPayment(false);
  };

  const openRecordPayment = async (client: ClientItem) => {
    setPaymentClient(client);
    setLoadingPaymentProjects(true);
    try {
      const detail = await fetchClientById(client.id) as ClientProjectsResponse;
      const projects = (detail.projects ?? [])
        .map((project) => ({ id: project.id, name: project.name }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setPaymentProjects(projects);
      setPaymentProjectId(projects[0]?.id ?? "");
    } catch (e) {
      toast({
        title: t("clients.failedToLoadProjects"),
        description: e instanceof Error ? e.message : t("clients.unableToPreparePaymentForm"),
        variant: "destructive",
      });
      closePaymentDialog();
    } finally {
      setLoadingPaymentProjects(false);
    }
  };

  const submitPayment = async () => {
    if (!paymentClient) return;
    if (!paymentProjectId) {
      toast({
        title: t("clients.projectRequired"),
        description: t("clients.selectProjectForPayment"),
        variant: "destructive",
      });
      return;
    }
    const amount = Number(paymentAmount || 0);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast({
        title: t("clients.invalidAmount"),
        description: t("clients.amountMustBeGreaterThanZero"),
        variant: "destructive",
      });
      return;
    }
    if (!paymentDate) {
      toast({
        title: t("clients.paymentDateRequired"),
        variant: "destructive",
      });
      return;
    }
    setSavingPayment(true);
    try {
      await createProjectPayment(paymentProjectId, {
        amount,
        paymentDate,
        method: paymentMethod,
        note: paymentNote.trim() || undefined,
      });
      await Promise.all([
        invalidateClientData(queryClient),
        invalidateProjectData(queryClient),
        queryClient.invalidateQueries({ queryKey: ["billing-overview"] }),
      ]);
      toast({ title: t("clients.paymentRecorded") });
      closePaymentDialog();
    } catch (e) {
      toast({
        title: t("clients.paymentFailed"),
        description: e instanceof Error ? e.message : t("clients.unableToRecordPayment"),
        variant: "destructive",
      });
    } finally {
      setSavingPayment(false);
    }
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <PageHeader
        title={
          <div className="flex items-center gap-2">
            <span>{t("pages.clients.title")}</span>
            <Popover open={clientHelpOpen} onOpenChange={setClientHelpOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-transparent text-muted-foreground transition-colors hover:bg-muted/30 hover:text-[#0C1F4F]"
                  aria-label={t("clients.help.ariaLabel")}
                >
                  <Info className="h-4 w-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-[340px] border-border/80 bg-white p-4 shadow-lg"
              >
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-[#0C1F4F]">{t("clients.help.title")}</p>
                    <p className="mt-1 text-sm leading-6 text-muted-foreground">{t("clients.help.subtitle")}</p>
                  </div>
                  <ul className="space-y-2 text-sm leading-6 text-muted-foreground">
                    <li>{t("clients.help.items.foundation")}</li>
                    <li>{t("clients.help.items.email")}</li>
                    <li>{t("clients.help.items.organization")}</li>
                    <li>{t("clients.help.items.contact")}</li>
                  </ul>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        }
        actions={
          <Button onClick={openCreateModal} className="bg-accent text-accent-foreground hover:bg-accent/90 gap-1.5">
            <Plus className="h-4 w-4" /> {t("clients.addClient")}
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-md w-full flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("clients.searchPlaceholder")}
            className="pl-9 h-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <RefreshingIndicator visible={isRefreshing} />
      </div>

      {error ? (
        <StateCard
          icon={AlertTriangle}
          tone="error"
          title={t("clients.failedToLoad")}
          description={error}
          primaryActionLabel={t("common.retry")}
          onPrimaryAction={() => void clientsQuery.refetch()}
        />
      ) : loading ? (
        <TableSkeleton rows={5} columns={5} />
      ) : clients.length === 0 ? (
        <StateCard
          icon={FileX}
          title={t("clients.noClients")}
          description={t("clients.noClientsDescription")}
        />
      ) : (
        <>
          <div className="hidden min-[1140px]:block rounded-lg border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="bg-muted/35 px-4 py-3 text-left text-[#0C1F4F]">{t("clients.clientName")}</th>
                  <th className="border-l border-border/60 px-4 py-3 text-left text-[#0C1F4F]">{t("clients.type")}</th>
                  <th className="border-l border-border/60 bg-muted/35 px-4 py-3 text-left text-[#0C1F4F]">Contact</th>
                  <th className="border-l border-border/60 bg-muted/35 px-4 py-3 text-left text-[#0C1F4F]">{t("clients.address")}</th>
                  <th className="w-[50px] border-l border-border/60 px-0 py-3 text-[#0C1F4F]"></th>
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => (
                  <tr
                    key={c.id}
                    className="border-b last:border-0 hover:bg-muted/30 cursor-pointer"
                    onClick={() => navigate(`/clients/${c.id}`)}
                  >
                    <td className="bg-muted/35 px-4 py-3">
                      <p className="font-semibold text-accent">{c.name}</p>
                      {c.companyName ? (
                        <p className="mt-0.5 text-xs text-muted-foreground truncate" title={c.companyName}>
                          {c.companyName}
                        </p>
                      ) : null}
                    </td>
                    <td className="border-l border-border/50 px-4 py-3 text-muted-foreground">{getClientTypeLabel(c.type)}</td>
                    {renderContactCell(c, true)}
                    <td className="border-l border-border/50 bg-muted/35 px-4 py-3 text-muted-foreground max-w-[240px] truncate">{c.address ?? "-"}</td>
                    <td
                      className="w-[50px] border-l border-border/50 p-0"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {renderClientActions(c, "flex h-full min-h-[72px] w-full items-center justify-center rounded-none hover:bg-secondary/60")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="hidden min-[767px]:block min-[1140px]:hidden space-y-3">
            {clients.map((c) => (
              <CompactClientCard
                key={`tablet-${c.id}`}
                client={c}
                onOpen={() => navigate(`/clients/${c.id}`)}
                actions={renderClientActions(c, "flex h-full min-h-[56px] w-12 items-center justify-center rounded-md hover:bg-secondary/60")}
              />
            ))}
          </div>

          <div className="min-[767px]:hidden space-y-3">
            {clients.map((c) => (
              <CompactClientCard
                key={`mobile-${c.id}`}
                client={c}
                onOpen={() => navigate(`/clients/${c.id}`)}
                actions={renderClientActions(c, "flex h-full min-h-[56px] w-12 items-center justify-center rounded-md hover:bg-secondary/60")}
              />
            ))}
          </div>
        </>
      )}

      <Dialog open={openModal} onOpenChange={(open) => !open && resetClientModalState()}>
        <DialogContent className="sm:max-w-2xl overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-[#0C1F4F]">
              {clientModalStep === "project"
                ? t("projects.addProject")
                : editingClient
                  ? t("clients.editClient")
                  : t("clients.addClient")}
            </DialogTitle>
            <DialogDescription className="text-[#0C1F4F]/80">
              {clientModalStep === "project"
                ? t("clients.continueSetupForClient", { client: flowClient?.name ?? t("clients.thisClient") })
                : t("clients.enterDetailsDescription")}
            </DialogDescription>
          </DialogHeader>

          <div className="overflow-hidden">
            <div
              className={`flex w-[200%] transition-transform duration-300 ease-out ${clientModalStep === "project" ? "-translate-x-1/2" : "translate-x-0"}`}
            >
              <div className="w-1/2 shrink-0 pr-2">
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-[#0C1F4F]">
                      {t("clients.clientName")} <span className="text-accent">*</span>
                    </Label>
                    <Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder={t("clients.clientNamePlaceholder")} />
                    {duplicateNameMatches.length > 0 ? (
                      <p className="text-xs text-amber-600">
                        {t("clients.similarClientExists", { names: duplicateNameMatches.slice(0, 2).map((c) => c.name).join(", ") })}
                      </p>
                    ) : null}
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[#0C1F4F]">{t("clients.companyName")}</Label>
                    <Input
                      value={form.companyName}
                      onChange={(e) => setForm((p) => ({ ...p, companyName: e.target.value }))}
                      placeholder={t("clients.companyNamePlaceholder")}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[#0C1F4F]">{t("clients.type")}</Label>
                    <Select value={form.type} onValueChange={(value) => setForm((p) => ({ ...p, type: value }))}>
                      <SelectTrigger>
                        <SelectValue placeholder={t("clients.selectType")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="General Contractor">{t("clients.types.generalContractor")}</SelectItem>
                        <SelectItem value="Owner">{t("clients.types.owner")}</SelectItem>
                        <SelectItem value="Property Manager">{t("clients.types.propertyManager")}</SelectItem>
                        <SelectItem value="Developer">{t("clients.types.developer")}</SelectItem>
                        <SelectItem value="Other">{t("clients.types.other")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-[#0C1F4F]">{t("clients.primaryApproverEmail")}</Label>
                      <Input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} placeholder={t("clients.primaryApproverEmailPlaceholder")} />
                      <p className="text-xs text-muted-foreground">
                        {t("clients.primaryApproverEmailHelp")}
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[#0C1F4F]">{t("common.phone")}</Label>
                      <Input
                        value={form.phone}
                        inputMode="numeric"
                        maxLength={14}
                        onChange={(e) => setForm((p) => ({ ...p, phone: formatUsPhoneDisplay(e.target.value) }))}
                        placeholder={t("clients.phonePlaceholder")}
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[#0C1F4F]">{t("clients.address")}</Label>
                    <Input value={form.address} onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))} placeholder={t("clients.addressPlaceholder")} />
                  </div>
                </div>

                <DialogFooter className="mt-5 gap-2.5">
                  <Button variant="outline" onClick={resetClientModalState}>{t("common.cancel")}</Button>
                  <Button
                    variant="outline"
                    onClick={() => void submitForm("save")}
                    disabled={saving || savingAndContinuing}
                  >
                    {saving ? t("common.saving") : editingClient ? t("common.saveChanges") : t("clients.addClient")}
                  </Button>
                  {!editingClient ? (
                    <Button
                      onClick={() => void submitForm("continue_to_project")}
                    disabled={saving || savingAndContinuing}
                    className="bg-accent text-accent-foreground hover:bg-accent/90"
                  >
                    {savingAndContinuing ? t("common.saving") : t("clients.continueToProject")}
                  </Button>
                ) : null}
                </DialogFooter>
              </div>

              <div className="w-1/2 shrink-0 pl-2">
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-[#0C1F4F]">
                      {t("projects.projectName")} <span className="text-accent">*</span>
                    </Label>
                    <Input
                      value={projectForm.name}
                      onChange={(e) => setProjectForm((p) => ({ ...p, name: e.target.value }))}
                      placeholder={t("projects.projectNamePlaceholder")}
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-[#0C1F4F]">{t("projects.projectCode")}</Label>
                      <Input
                        value={projectForm.code}
                        onChange={(e) => setProjectForm((p) => ({ ...p, code: e.target.value }))}
                        placeholder={t("projects.projectCodePlaceholder")}
                      />
                    </div>
                    <div className="space-y-1.5 sm:pt-0.5">
                      <Label className="text-[#0C1F4F]">
                        {t("clients.clientName")} <span className="text-accent">*</span>
                      </Label>
                      <div className="flex h-9 items-center rounded-md border bg-muted/20 px-3 text-sm text-foreground">
                        {flowClient?.name ?? t("clients.selectedClient")}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-[#0C1F4F]">{t("projects.jobSiteAddress")}</Label>
                    <Input
                      value={projectForm.jobSiteAddress}
                      onChange={(e) => setProjectForm((p) => ({ ...p, jobSiteAddress: e.target.value }))}
                      placeholder={t("clients.addressPlaceholder")}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#0C1F4F]">{t("projects.labels")}</Label>
                    {projectForm.labelIds.length ? (
                      <div className="flex flex-wrap gap-1.5">
                        {projectForm.labelIds
                          .map((labelId) => projectLabels.find((label) => label.id === labelId))
                          .filter((label): label is ProjectLabel => Boolean(label))
                          .map((label) => (
                            <span
                              key={label.id}
                              className="inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium"
                              style={{
                                color: label.color,
                                borderColor: `${label.color}40`,
                                backgroundColor: `${label.color}14`,
                              }}
                            >
                              <span
                                className="mr-1 h-1.5 w-1.5 rounded-full"
                                style={{ backgroundColor: label.color }}
                              />
                              {label.name}
                            </span>
                          ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">{t("projects.noLabelsSelected")}</p>
                    )}

                    {projectLabels.length ? (
                      <>
                        <Input
                          value={projectLabelSearch}
                          onChange={(e) => setProjectLabelSearch(e.target.value)}
                          placeholder={t("projects.searchLabels")}
                        />
                        <div className="max-h-40 overflow-y-auto rounded-md border">
                          {filteredProjectLabels.length ? (
                            filteredProjectLabels.map((label) => {
                              const checked = projectForm.labelIds.includes(label.id);
                              return (
                                <label
                                  key={label.id}
                                  className="flex cursor-pointer items-center justify-between gap-3 border-b px-3 py-2 text-sm last:border-b-0 hover:bg-muted/30"
                                >
                                  <span className="flex min-w-0 items-center gap-2">
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={() =>
                                        setProjectForm((prev) => ({
                                          ...prev,
                                          labelIds: checked
                                            ? prev.labelIds.filter((id) => id !== label.id)
                                            : [...prev.labelIds, label.id],
                                        }))
                                      }
                                    />
                                    <span
                                      className="inline-flex h-2 w-2 rounded-full"
                                      style={{ backgroundColor: label.color }}
                                    />
                                    <span className="truncate">{label.name}</span>
                                  </span>
                                  <span
                                    className="inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium"
                                    style={{
                                      color: label.color,
                                      borderColor: `${label.color}40`,
                                      backgroundColor: `${label.color}14`,
                                    }}
                                  >
                                    {label.name}
                                  </span>
                                </label>
                              );
                            })
                          ) : (
                            <div className="px-3 py-4 text-center text-xs text-muted-foreground">
                              {t("projects.noLabelsMatch")}
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        {t("projects.createLabelsInSettings")}
                      </p>
                    )}
                  </div>
                </div>

                <DialogFooter className="mt-5 gap-2.5">
                  <Button variant="outline" onClick={resetClientModalState}>{t("common.cancel")}</Button>
                  <Button
                    variant="outline"
                    onClick={() => void submitProjectStep("save")}
                    disabled={savingProject || savingProjectAndContinuing}
                  >
                    {savingProject ? t("common.saving") : t("projects.addProject")}
                  </Button>
                  <Button
                    onClick={() => void submitProjectStep("continue_to_ticket")}
                    disabled={savingProject || savingProjectAndContinuing}
                    className="bg-accent text-accent-foreground hover:bg-accent/90"
                  >
                    {savingProjectAndContinuing ? t("common.saving") : t("projects.continueToTicket")}
                  </Button>
                </DialogFooter>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("clients.deleteClient")}</DialogTitle>
            <DialogDescription>
              {deleteTarget
                ? t("clients.confirmDeleteClient", { name: deleteTarget.name })
                : t("common.areYouSure")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>{t("common.cancel")}</Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={deleting} className="gap-1.5">
              <Trash2 className="h-4 w-4" /> {deleting ? t("common.deleting") : t("common.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!paymentClient} onOpenChange={(open) => !open && closePaymentDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("clients.recordPaymentForClient")}</DialogTitle>
            <DialogDescription>
              {paymentClient
                ? t("clients.recordPaymentForName", { name: paymentClient.name })
                : t("clients.recordPayment")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>{t("common.project")} *</Label>
              {loadingPaymentProjects ? (
                <div className="h-9 rounded-md border bg-muted/20 px-3 text-sm text-muted-foreground flex items-center">
                  {t("clients.loadingProjects")}
                </div>
              ) : paymentProjects.length === 0 ? (
                <div className="rounded-md border bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
                  {t("clients.noProjectFoundForClient")}
                </div>
              ) : (
                <select
                  className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                  value={paymentProjectId}
                  onChange={(event) => setPaymentProjectId(event.target.value)}
                >
                  {paymentProjects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("clients.amount")} *</Label>
                <Input
                  type="number"
                  min={0}
                  value={paymentAmount}
                  onChange={(event) => setPaymentAmount(event.target.value)}
                  placeholder={t("clients.amountPlaceholder")}
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t("clients.paymentDate")} *</Label>
                <Input
                  type="date"
                  value={paymentDate}
                  onChange={(event) => setPaymentDate(event.target.value)}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("clients.method")}</Label>
              <select
                className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                value={paymentMethod}
                onChange={(event) => setPaymentMethod(event.target.value as ProjectPaymentMethod)}
              >
                {PAYMENT_METHOD_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.label)}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("clients.note")}</Label>
              <Input
                value={paymentNote}
                onChange={(event) => setPaymentNote(event.target.value)}
                placeholder={t("clients.optionalNote")}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closePaymentDialog}>{t("common.cancel")}</Button>
            <Button
              onClick={submitPayment}
              disabled={savingPayment || loadingPaymentProjects || paymentProjects.length === 0}
              className="bg-accent text-accent-foreground hover:bg-accent/90"
            >
              {savingPayment ? t("common.saving") : t("clients.recordPayment")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
